import React, { useState, useEffect, useCallback, useMemo, useRef, Suspense } from "react";
import QRpop from "./qrpop";
import DOSPrompt from "./components/win/DOSPrompt";
import WelcomeWarning from "./components/WelcomeWarning";
import { getPrograms } from "./components/win/programs";
import WindowManager from "./components/win/WindowManager";
import { WindowDataProvider } from "./components/win/includes/WindowContext";
import { SKClientProviderManager } from "./components/contexts/SKClientProviderManager";
import { Toaster } from "react-hot-toast";
import { StateSetterProvider } from "./components/contexts/SetterContext";
import { PrivacyThemeProvider } from "./components/toolbox/privacy-extensions";
import "./components/toolbox/privacy-extensions/theme/privacyTheme.css";
import "./styles/win95.css";
import { AssetValue } from "@doritokit/helpers";
import AfterDarkScreensaver from "./components/win/AfterDarkScreensaver";
import { copyToClipboard } from "./components/win/includes/utils";
import StagingBanner from "./components/StagingBanner";
import { createLogger } from "./utils/logger.js";
import { sweepLegacyFromtLocalStorage } from "./components/toolbox/monero-extensions/fromt/fromtFrostVaultIntegration";
import { sweepLegacyOrchardLocalStorage } from "./components/toolbox/zcash-extensions/frozt/orchardFrostVaultIntegration";
import { sweepLegacySaplingLocalStorage } from "./components/toolbox/zcash-extensions/frozt/froztVaultIntegration";
import { sweepLegacyShieldedLocalStorage } from "./components/toolbox/zcash-extensions/utils/shielded-transaction-client";
import { sweepLegacyMoneroBalanceCache } from "./components/toolbox/monero-extensions/utils/moneroBalanceCache";
import { sweepLegacyMoneroRescanCache } from "./components/toolbox/monero-extensions/utils/moneroRescanCache";
import { clearAllWinbit32Storage } from "./services/winbit32Storage";
import { clearPrivacyThemeRuntime } from "./components/toolbox/privacy-extensions";
import { hydrateScreensaverSettingsFromStorage } from "./services/screensaverSettings";
import useScreensaverIdle from "./components/win/useScreensaverIdle";

// `ZcashTestHarness` is only ever rendered when the URL points at the
// dev/test mode (`?zcashTestAutoRun=...`). Loading it eagerly forced a
// huge dependency closure (Zcash WASM scanner client, full FROST stack,
// etc.) into the main bundle on every visit. Lazy-loading drops ~8MB
// from the first paint payload.
const ZcashTestHarness = React.lazy(
	() => import(/* webpackChunkName: "app-zcash-test" */ "./components/apps/winbit32/ZcashTestHarness"),
);

const log = createLogger('App');

const programs = getPrograms();

const App = () => {
	const [qrResult, setQrResult] = useState(null);
	const [showQRPop, setShowQRPop] = useState(false);
	const [showDOSPrompt, setShowDOSPrompt] = useState(false);
	const [minimizedWindows, setMinimizedWindows] = useState([]);
	const [windowHistory, setWindowHistory] = useState([]);
	const [windows, setWindows] = useState([]);
	const [contextMenuVisible, setContextMenuVisible] = useState(false);
	const [contextMenuPosition, setContextMenuPosition] = useState({
		x: 0,
		y: 0,
	});
	const [highestZIndex, setHighestZIndex] = useState(1);
	const [loaded, setLoaded] = useState(false);
	const [license, setLicense] = useState(false);
	const [appData, setAppData] = useState({});
	const [showScreensaver, setShowScreensaver] = useState(false);
	const isZcashTestStandalone = useMemo(() => {
		if (typeof window === "undefined") return false;
		try {
			const params = new URLSearchParams(window.location.search);
			const mode = (params.get("zcashTestStandalone") || "").toLowerCase();
			return mode === "1" || mode === "true" || mode === "standalone";
		} catch (_error) {
			return false;
		}
	}, []);

	const hashPathRef = useRef(null);
	const initialHashRef = useRef(typeof window !== "undefined" ? window.location.hash : "");

	log.debug('window', typeof window !== 'undefined' ? 'browser' : 'ssr');

	AssetValue.loadStaticAssets();

	// Once-per-tab privacy sweep: wipe any legacy entries that older
	// builds wrote to `localStorage`. Wallet key material, shielded
	// notes, nullifiers and per-address balance caches now all live
	// in-memory only and are persisted exclusively via explicit save
	// actions (`.wult` file download). Loading `.vult` files in a
	// fresh tab no longer surfaces stale data from previous sessions.
	useEffect(() => {
		try {
			sweepLegacyFromtLocalStorage();
			sweepLegacyOrchardLocalStorage();
			sweepLegacySaplingLocalStorage();
			sweepLegacyShieldedLocalStorage();
			sweepLegacyMoneroBalanceCache();
			sweepLegacyMoneroRescanCache();
		} catch (err) {
			log.warn('legacy privacy-cache sweep failed:',
				err?.message || err);
		}
	}, []);

	// Tell the boot splash (see public/index.html) that React has
	// rendered. The MutationObserver in index.html will pick up the
	// first DOM mutation under #root anyway; setting the body class
	// here is the explicit signal so we control timing.
	useEffect(() => {
		if (typeof document !== 'undefined') {
			document.body.classList.add('wb32-app-ready');
		}
	}, []);

	const handleExit = () => {
		// "Exit" must leave no trace: wipe every WINBIT32-owned
		// localStorage key (theme, imported tokens, window state,
		// legacy FROST bundles, dev flags, RareSats cache, …) and
		// reset the in-memory theme runtime so the user sees the
		// bundled defaults the next time they boot.
		try {
			const removed = clearAllWinbit32Storage();
			if (removed > 0) {
				log.info('Exit: cleared', removed, 'stored items');
			}
		} catch (err) {
			log.warn('Exit storage wipe failed:', err?.message || err);
		}
		try { clearPrivacyThemeRuntime(); }
		catch (err) { log.warn('Exit theme reset failed:', err?.message || err); }
		setShowDOSPrompt(true);
	};

	// Clear imported tokens on page unload/close
	useEffect(() => {
		const handleBeforeUnload = () => {
			try {
				localStorage.removeItem('winbit32_importedTokens');
			} catch (_e) {
				// Storage operation failed, continue
			}
		};

		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => window.removeEventListener('beforeunload', handleBeforeUnload);
	}, []);

	const setStateAndSave = (newState) => {
		setQrResult(newState.qrResult || qrResult);
		setShowQRPop(newState.showQRPop || showQRPop);
		setShowDOSPrompt(newState.showDOSPrompt || showDOSPrompt);
		setMinimizedWindows(newState.minimizedWindows || minimizedWindows);
		setWindowHistory(newState.windowHistory || windowHistory);
		setWindows(newState.windows || windows);
		setContextMenuVisible(newState.contextMenuVisible || contextMenuVisible);
		setContextMenuPosition(newState.contextMenuPosition || contextMenuPosition);
		setHighestZIndex(newState.highestZIndex || highestZIndex);
		setLicense(newState.license || license);
	};

	const toggleQRPop = () => {
		setShowQRPop(!showQRPop);
	};

	const handleQRRead = (result) => {
		setQrResult(result);
		setShowQRPop(false);
	};

	useEffect(() => {
		// if (windowName === "desktop") {

		//get hash, split by /, if the first one begins ~ then set option with it then remove it
		const hash = initialHashRef.current || window.location.hash;

		if(!hash){
			hashPathRef.current = [];
		}else{
			const hashParts = hash.replace("#", "").split("/");
			log.debug("Hash Parts:", hashParts);
			if (hashParts[0].startsWith("~")) {
				const option = hashParts[0].replace("~", "");
				//split by =
				const optionParts = option.split("=");
				const validOptions = ["embedMode"];
				if(optionParts[1] !== "true" && optionParts[1] !== "false"){
					//base64 decode
					optionParts[1] = atob(optionParts[1]);
				}
				if(validOptions.includes(optionParts[0])){
					setAppDataKey(optionParts[0], optionParts[1]);
				}
				hashParts.shift();
				window.history.replaceState(null, null, `#${hashParts.join("/")}`);
			}
			hashPathRef.current = hashParts;
		}
		setTimeout(() => {
			setLoaded(true);
		}, 1500);
	}, []);

	const sendUpHash = (hashParts, windowId) => {
			//reverse hashParts so that the first part is the top level
			const rHashParts = hashParts.slice().reverse();
			let newHash = hashParts.length ? `#${rHashParts.join("/")}` : "";
			if( newHash === "#progman"){
				newHash = "";
			}
			if (window.location.hash !== newHash) {
				window.history.replaceState(null, null, newHash);
			}
		};

	const setAppDataKey = (key, value) => {
		setAppData((prev) => {
			return { ...prev, [key]: value };
		}
		);
	};

	useEffect(() => {
		hydrateScreensaverSettingsFromStorage();
	}, []);

	// Idle timer, per Control Panel → Display. Off when the delay is 0.
	useScreensaverIdle(showScreensaver, useCallback(() => {
		setShowScreensaver(true);
	}, []));

	useEffect(() => {
		setAppDataKey("setAppDataKey", setAppDataKey);
		setAppDataKey("license", license);
		setAppDataKey("setLicense", setLicense);
		// Prefer setShowScreensaver; keep setShowMatrix as an alias so
		// older call sites (context menu) keep working.
		setAppDataKey("setShowScreensaver", setShowScreensaver);
		setAppDataKey("setShowMatrix", setShowScreensaver);
	}
	, [setAppData, license, setShowScreensaver]);

    // Global two-click copy functionality for areas with text (div/span/p/contentEditable)
	useEffect(() => {
        let lastClickTime = 0;
        let lastClickTarget = null;

        const isClickable = (el) => !!(el && el.closest && el.closest('button, [role="button"], a, .swap-toolbar-button, .action-button'));
        const isTextAreaLike = (el) => {
            if (!el) return false;
            const tag = el.tagName ? el.tagName.toLowerCase() : '';
            if (tag === 'input' || tag === 'textarea') return false; // skip form inputs
            if (el.contentEditable === 'true') return true;
            return (tag === 'div' || tag === 'span' || tag === 'p') && (el.textContent?.trim()?.length > 0);
        };

        const onClick = (event) => {
            const target = event.target;
            if (!isTextAreaLike(target)) { lastClickTarget = target; lastClickTime = Date.now(); return; }
            if (isClickable(target)) { lastClickTarget = target; lastClickTime = Date.now(); return; }

            const now = Date.now();
            if (target === lastClickTarget && (now - lastClickTime) < 2000 && (now - lastClickTime) > 1000) {
                // Second click within 2s on same element: copy
                const textToCopy = (target.textContent || target.innerText || '').trim();
                if (textToCopy) copyToClipboard(textToCopy);
                // Prevent focusing issues
                event.preventDefault();
                event.stopPropagation();
            }
            lastClickTarget = target;
            lastClickTime = now;
        };

        document.addEventListener('click', onClick, true);
        return () => document.removeEventListener('click', onClick, true);
	}, []);

	const { embedMode } = appData || {};

	return (
		<SKClientProviderManager>
			{/*
			 * Mount the Privacy Suite theme provider at the desktop
			 * root. Only the Privacy Suite + its wizards opt into the
			 * `--privacy-*` design tokens, but hoisting the provider
			 * here means:
			 *
			 *   • a single instance owns the theme state and
			 *     localStorage write, so the View → Theme toggle
			 *     stays in sync even if the user opens a wizard via
			 *     a deep-link hash before the Privacy Suite mounts;
			 *   • non-private apps (Money Manager, Exora, etc) sit
			 *     inside the provider too but ignore it — they don't
			 *     reference any `--privacy-*` variable and aren't
			 *     scoped by the `[data-privacy-theme]` selector, so
			 *     their grey Win 3.1 chrome stays untouched.
			 */}
			<PrivacyThemeProvider>
			<StateSetterProvider>
				{isZcashTestStandalone ? (
					<div
						style={{
							minHeight: "100vh",
							padding: "16px",
							background: "#f2f2f2",
						}}>
						<Suspense fallback={<div style={{ padding: 16 }}>Loading Zcash test harness…</div>}>
							<ZcashTestHarness />
						</Suspense>
					</div>
				) : (
					<>
						<StagingBanner />
						{showQRPop && (
							<QRpop onQRRead={handleQRRead} closeQrPop={toggleQRPop} />
						)}
						{showDOSPrompt ? (
							<DOSPrompt />
						) : (
							<>
								{hashPathRef.current !== null && (
									<div
										style={loaded ? { zIndex: 999 } : { zIndex: 0 }}
										className="full-desktop"
										id="desktop">
										<WindowDataProvider>
											<WindowManager
												programs={programs}
												windowName={"desktop"}
												setStateAndSave={setStateAndSave}
												providerKey={"desktop"}
												handleOpenArray={[]}
												handleExit={handleExit}
												sendUpHash={sendUpHash}
												windowId={"desktop"}
												hashPath={hashPathRef.current}
												appData={appData}
											/>
										</WindowDataProvider>
									</div>
								)}
								{loaded ? (
									!embedMode && (
										<WelcomeWarning onExit={handleExit} />
									)
								) : (
									<div
										className="loading_overlay"
										id="loading_overlay"
										onClick={() => {
											setLoaded(true);
											document.getElementById("loading_overlay").style.display =
												"none";
										}}>
										<div>
											<img
												src={process.env.PUBLIC_URL + "/winlogo2.png"}
												alt="logo"
											/>
										</div>
									</div>
								)}
							</>
						)}
					</>
				)}
				
				<Toaster
					position="bottom-right"
					containerStyle={{
						zIndex: 999000000000000000,
					}}
					toastOptions={{
						// Define default options
						className: "toast",
						duration: 3000,
						style: {
							paddingTop: "20px",
						},
					}}
				/>
				<AfterDarkScreensaver
					show={showScreensaver}
					onHide={() => setShowScreensaver(false)}
				/>
			</StateSetterProvider>
			</PrivacyThemeProvider>
		</SKClientProviderManager>
	);
};

export default App;
